import { useState, useRef, useEffect, useMemo } from 'react'
import type { FormEvent, ReactElement, KeyboardEvent } from 'react'
import {
  parsePhoneNumberWithError,
  parsePhoneNumberFromString,
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'

const COMMON_COUNTRIES: Array<{ code: CountryCode; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'KR', name: 'South Korea' },
]

// Get country flag emoji
function getCountryFlag(code: string): string {
  const codePoints = code
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

export type PhoneInputMeta = {
  country: string | null
  e164: string | null
  formattedNational: string
  isValid: boolean
}

export interface PhoneInputProps {
  name: string
  id?: string
  label?: string
  value?: string // E.164
  defaultValue?: string // E.164
  onChange?: (value: string | null, meta: PhoneInputMeta) => void
  required?: boolean
  disabled?: boolean
  placeholder?: string
  defaultCountry?: string // ISO code
  availableCountries?: string[]
  className?: string
  borderStyle?: string
  labelStyle?: string
  inputClassName?: string
  dropdownClassName?: string
  error?: string
  icon?: ReactElement | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  onBlur?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

export default function PhoneInput({
  name,
  id,
  label,
  value: controlledValue,
  defaultValue,
  onChange,
  required = false,
  disabled = false,
  placeholder,
  defaultCountry = 'US',
  availableCountries,
  className,
  borderStyle,
  labelStyle,
  inputClassName,
  dropdownClassName,
  error,
  icon,
  size,
  onBlur,
  onKeyDown,
}: PhoneInputProps) {
  const isControlled = controlledValue !== undefined
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>((defaultCountry as CountryCode) || 'US')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [formattedNational, setFormattedNational] = useState('')
  const [e164Value, setE164Value] = useState<string | null>(defaultValue || controlledValue || null)
  const [isValid, setIsValid] = useState(true)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousValueRef = useRef<string | null>(null)

  // Get all countries, filtered by availableCountries if provided
  const countries = useMemo(() => {
    const allCountryCodes = availableCountries
      ? (availableCountries.filter((c) => getCountries().includes(c as CountryCode)) as CountryCode[])
      : getCountries()
    const commonCountrySet = new Set(COMMON_COUNTRIES.map((c) => c.code))

    const allCountries: Array<{ code: CountryCode; name: string; flag: string }> = []

    // Add common countries first (if they're in the available list)
    COMMON_COUNTRIES.forEach((country) => {
      if (allCountryCodes.includes(country.code)) {
        allCountries.push({ ...country, flag: getCountryFlag(country.code) })
      }
    })

    // Add remaining countries
    allCountryCodes.forEach((code) => {
      if (!commonCountrySet.has(code)) {
        const countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code
        allCountries.push({ code, name: countryName, flag: getCountryFlag(code) })
      }
    })

    return allCountries
  }, [availableCountries])

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries
    const query = searchQuery.toLowerCase()
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query) ||
        getCountryCallingCode(country.code).includes(query),
    )
  }, [countries, searchQuery])

  // Format national number (without country code)
  const formatNationalNumber = (digits: string, country: CountryCode): string => {
    if (!digits) return ''
    const formatter = new AsYouType(country)
    return formatter.input(digits)
  }

  // Convert to E.164 format
  const convertToE164 = (nationalDigits: string, country: CountryCode): string | null => {
    if (!nationalDigits || nationalDigits.trim().length === 0) {
      return null
    }

    try {
      // Combine country code with national digits
      const countryCode = getCountryCallingCode(country)
      const fullNumber = `+${countryCode}${nationalDigits.replace(/\D/g, '')}`
      const phoneNumber = parsePhoneNumberWithError(fullNumber)
      return phoneNumber.number
    } catch {
      // If parsing fails, try to construct E.164 manually
      const digits = nationalDigits.replace(/\D/g, '')
      if (digits.length === 0) return null
      const countryCode = getCountryCallingCode(country)
      return `+${countryCode}${digits}`
    }
  }

  // Parse E.164 value and extract national number and country
  const parseE164Value = (e164: string | null): { nationalNumber: string; country: CountryCode } | null => {
    if (!e164 || !e164.startsWith('+')) {
      return null
    }

    try {
      const phoneNumber = parsePhoneNumberFromString(e164)
      if (phoneNumber && phoneNumber.country) {
        return {
          nationalNumber: phoneNumber.nationalNumber,
          country: phoneNumber.country,
        }
      }
    } catch {
      // Parsing failed
    }

    return null
  }

  // Sync state from value/defaultValue prop changes
  useEffect(() => {
    const currentValue = isControlled ? controlledValue : defaultValue
    const valueToUse = currentValue || null

    // Only update if the value actually changed
    if (valueToUse !== previousValueRef.current) {
      previousValueRef.current = valueToUse

      if (valueToUse) {
        const parsed = parseE164Value(valueToUse)
        if (parsed) {
          setSelectedCountry(parsed.country)
          const formatted = formatNationalNumber(parsed.nationalNumber, parsed.country)
          setFormattedNational(formatted)
          setE164Value(valueToUse)
          const valid = isValidPhoneNumber(valueToUse)
          setIsValid(valid)
        } else {
          setFormattedNational('')
          setE164Value(null)
          setIsValid(true)
        }
      } else {
        setFormattedNational('')
        setE164Value(null)
        setIsValid(true)
      }
    }
  }, [controlledValue, defaultValue, isControlled])

  // Handle input change
  function handleInput(event: FormEvent<HTMLInputElement>) {
    if (disabled) return

    const input = event.currentTarget
    const inputValue = input.value
    const cursorPosition = input.selectionStart || 0

    // Check if pasted value contains E.164 format
    const nativeEvent = event.nativeEvent as InputEvent
    const pastedText = nativeEvent.data || ''

    // If pasted text starts with +, try to parse it globally
    if (pastedText.startsWith('+') || inputValue.startsWith('+')) {
      const toParse = pastedText.startsWith('+') ? pastedText : inputValue
      const parsed = parseE164Value(toParse)
      if (parsed) {
        setSelectedCountry(parsed.country)
        const formatted = formatNationalNumber(parsed.nationalNumber, parsed.country)
        setFormattedNational(formatted)
        const e164 = convertToE164(parsed.nationalNumber, parsed.country)
        setE164Value(e164)
        previousValueRef.current = e164
        const valid = e164 ? isValidPhoneNumber(e164) : false
        setIsValid(valid)

        // Call onChange
        const meta: PhoneInputMeta = {
          country: parsed.country,
          e164: e164,
          formattedNational: formatted,
          isValid: valid,
        }
        onChange?.(e164, meta)

        // Restore cursor position
        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newPosition = Math.max(formatted.length, cursorPosition)
            inputRef.current.setSelectionRange(newPosition, newPosition)
          }
        })
        return
      }
    }

    // Extract digits only (no country code in input)
    const digits = inputValue.replace(/\D/g, '')
    const formatted = formatNationalNumber(digits, selectedCountry)
    setFormattedNational(formatted)

    const e164 = convertToE164(digits, selectedCountry)
    setE164Value(e164)
    previousValueRef.current = e164

    // Validate
    const valid = e164 ? isValidPhoneNumber(e164) : true
    setIsValid(valid)

    // Call onChange
    const meta: PhoneInputMeta = {
      country: selectedCountry,
      e164: e164,
      formattedNational: formatted,
      isValid: valid,
    }
    onChange?.(e164, meta)

    // Restore cursor position
    requestAnimationFrame(() => {
      if (inputRef.current) {
        // Calculate new cursor position based on digit count
        const digitsBeforeCursor = inputValue.substring(0, cursorPosition).replace(/\D/g, '').length
        let newPosition = 0
        let digitCount = 0

        for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++
          }
          newPosition = i + 1
        }

        inputRef.current.setSelectionRange(newPosition, newPosition)
      }
    })
  }

  // Handle country selection
  function handleCountrySelect(countryCode: CountryCode) {
    if (disabled) return

    setSelectedCountry(countryCode)
    setIsDropdownOpen(false)
    setSearchQuery('')

    // Re-format current national number with new country
    const digits = formattedNational.replace(/\D/g, '')
    const formatted = formatNationalNumber(digits, countryCode)
    setFormattedNational(formatted)

    const e164 = convertToE164(digits, countryCode)
    setE164Value(e164)
    previousValueRef.current = e164

    // Validate
    const valid = e164 ? isValidPhoneNumber(e164) : true
    setIsValid(valid)

    // Call onChange
    const meta: PhoneInputMeta = {
      country: countryCode,
      e164: e164,
      formattedNational: formatted,
      isValid: valid,
    }
    onChange?.(e164, meta)

    inputRef.current?.focus()
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleBlur() {
    if (e164Value) {
      const valid = isValidPhoneNumber(e164Value)
      setIsValid(valid)
    }
    onBlur?.()
  }

  // Size styles
  const inputId = id ?? name

  let sizePadding = ''
  let sizeText = ''
  let iconPaddingLeft = ''
  let labelTextSize = ''

  switch (size) {
    case 'xs':
      sizePadding = 'px-2.5 py-1.5'
      sizeText = 'text-xs'
      iconPaddingLeft = icon ? ' pl-8' : ' pl-2.5'
      labelTextSize = 'text-xs'
      break
    case 'sm':
      sizePadding = 'px-3 py-2'
      sizeText = 'text-sm'
      iconPaddingLeft = icon ? ' pl-9' : ' pl-3'
      labelTextSize = 'text-sm'
      break
    case 'md':
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      iconPaddingLeft = icon ? ' pl-10' : ' pl-3.5'
      labelTextSize = 'text-base'
      break
    case 'lg':
      sizePadding = 'px-4 py-3'
      sizeText = 'text-lg'
      iconPaddingLeft = icon ? ' pl-11' : ' pl-4'
      labelTextSize = 'text-lg'
      break
    case 'xl':
      sizePadding = 'px-5 py-3.5'
      sizeText = 'text-xl'
      iconPaddingLeft = icon ? ' pl-12' : ' pl-5'
      labelTextSize = 'text-xl'
      break
    default:
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      iconPaddingLeft = icon ? ' pl-10' : ' pl-3.5'
      labelTextSize = 'text-sm'
  }

  const hasValue = formattedNational && formattedNational.trim().length > 0

  const baseInputStyles =
    'block w-full rounded-md text-neutral-800 shadow-sm transition-colors focus:outline-none bg-white font-sans font-extralight'

  // Border styles
  let borderStyles = borderStyle || ''
  const hasError = error || (!isValid && hasValue)
  if (hasError) {
    borderStyles += 'border !border-red-500 focus-within:ring-2 focus-within:ring-cta focus-within:border-transparent'
  } else {
    borderStyles += 'border border-slate-400 focus-within:ring-2 focus-within:ring-cta focus-within:border-transparent'
  }

  // Placeholder styles
  const defaultPlaceholderStyles = 'placeholder:text-neutral-400'
  const finalPlaceholderStyles = inputClassName || defaultPlaceholderStyles

  const defaultContainerStyle = 'flex flex-col gap-2 w-full'
  const finalContainerStyle = className || defaultContainerStyle

  const labelColor = hasValue ? 'text-neutral-800' : 'text-neutral-400'
  const baseLabelStyles = `block font-semibold leading-6 ${labelColor} ${labelTextSize} font-sans`
  const defaultLabelStyle = label ? baseLabelStyles : ''

  const iconContainerStyles = 'absolute left-3 top-1/2 -translate-y-1/2 text-neutral-800'

  const selectedCountryData = countries.find((c) => c.code === selectedCountry) || {
    code: (defaultCountry as CountryCode) || 'US',
    name: COMMON_COUNTRIES.find((c) => c.code === defaultCountry)?.name || 'United States',
    flag: getCountryFlag(defaultCountry || 'US'),
  }

  return (
    <div className={finalContainerStyle}>
      {label && (
        <label htmlFor={inputId} className={labelStyle || defaultLabelStyle}>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}
      <div className='relative w-full min-w-0'>
        <div className={`relative flex items-center ${baseInputStyles} ${borderStyles} ${sizeText} sm:leading-6`}>
          <div className='relative flex-shrink-0' ref={dropdownRef}>
            <button
              type='button'
              onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
              disabled={disabled}
              className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-l-md border-r border-slate-300 ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label='Select country'
              aria-expanded={isDropdownOpen}
              aria-haspopup='listbox'
            >
              <span className='text-base leading-none'>{selectedCountryData.flag}</span>
              <span className='text-sm text-secondary-txt'>+{getCountryCallingCode(selectedCountry)}</span>
              <svg
                className={`w-3 h-3 text-secondary-txt transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                className={`absolute z-50 mt-1 w-80 max-h-96 bg-white border border-slate-300 rounded-md shadow-lg overflow-hidden top-full left-0 ${
                  dropdownClassName || ''
                }`}
                role='listbox'
              >
                <div className='p-2 border-b border-slate-200'>
                  <input
                    type='text'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder='Search country...'
                    className='w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none'
                    autoFocus
                    aria-label='Search countries'
                  />
                </div>

                {/* Country List */}
                <div className='max-h-80 overflow-y-auto'>
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type='button'
                      onClick={() => handleCountrySelect(country.code)}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 ${
                        selectedCountry === country.code ? 'bg-cta/10' : ''
                      }`}
                      role='option'
                      aria-selected={selectedCountry === country.code}
                    >
                      <span className='text-lg leading-none'>{country.flag}</span>
                      <span className='flex-1 text-sm text-neutral-800'>{country.name}</span>
                      <span className='text-xs text-neutral-500'>+{getCountryCallingCode(country.code)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Phone Input - National Number Only */}
          <div className='relative flex-1 min-w-0'>
            {icon && <div className={iconContainerStyles}>{icon}</div>}
            <input
              ref={inputRef}
              type='tel'
              id={inputId}
              autoComplete='tel'
              onInput={handleInput}
              onBlur={handleBlur}
              onKeyDown={onKeyDown}
              value={formattedNational}
              disabled={disabled}
              required={required}
              className={`w-full min-w-0 ${sizePadding} ${finalPlaceholderStyles} focus:outline-none bg-transparent border-0 ${iconPaddingLeft} ${
                inputClassName || ''
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              placeholder={placeholder}
            />
          </div>
        </div>
      </div>

      {/* Hidden input for form submission (E.164 value) */}
      <input type='hidden' name={name} value={e164Value || ''} />
    </div>
  )
}
